import React from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    ArrowRight,
    Calendar,
    Car,
    Clock,
    Loader2,
    Send,
    User,
} from "lucide-react";
import { DURUM_LABELS, DURUM_STYLES } from "./onTahkimConstants";

function DurumBadge({ durum }) {
    const key = durum || "BEKLIYOR";
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-semibold uppercase ${
                DURUM_STYLES[key] || DURUM_STYLES.BEKLIYOR
            }`}
        >
            {DURUM_LABELS[key] || key}
        </span>
    );
}

export function BekleyenKart({ row, acting, onUygula, selected, onToggleSelect }) {
    const kapali =
        row.dosya_durumu === "KAPATILDI" || row.dosya_durumu === "KAPATILDI";
    const canSelect =
        !kapali &&
        (row.hazir ||
            row.on_tahkim_sure_durumu === "HAZIR" ||
            row.on_tahkim_sure_durumu === "GECIKTI");
    const canApply = canSelect;

    return (
        <article className="group rounded-xl border border-white/10 bg-gradient-to-br from-[#121A2F] to-[#0B0F19]/90 p-4 hover:border-sky-500/35 transition-all">
            <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                    <Link
                        to={`/e/sigorta-dosyalari/${row.sigorta_dosya_id}`}
                        className="text-sm font-mono text-[#F3E5AB] hover:underline block truncate"
                    >
                        {row.dosya_no || `#${row.sigorta_dosya_id}`}
                    </Link>
                    <div className="flex items-center gap-1 text-[10px] text-slate-500 mt-0.5">
                        <Car className="h-3 w-3" />
                        {row.plaka || "—"}
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    {canSelect && (
                        <input
                            type="checkbox"
                            checked={selected}
                            onChange={onToggleSelect}
                            className="rounded border-white/20"
                            title="Toplu geçiş için seç"
                        />
                    )}
                    <DurumBadge durum={row.on_tahkim_sure_durumu} />
                </div>
            </div>

            <div className="space-y-1.5 text-xs">
                <div className="flex items-center gap-1.5 text-slate-400">
                    <User className="h-3 w-3 shrink-0" />
                    <span className="truncate">{row.muvekkil_ad || "—"}</span>
                </div>
                <div className="text-slate-500 truncate">{row.sigorta_sirketi || "—"}</div>
            </div>

            <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-2 gap-2 text-[10px]">
                <div>
                    <div className="text-slate-600 uppercase tracking-wide">Gönderim</div>
                    <div className="font-mono text-slate-300 mt-0.5">
                        {row.basvuru_gonderim_tarihi || "—"}
                    </div>
                </div>
                <div>
                    <div className="text-slate-600 uppercase tracking-wide">Hedef</div>
                    <div className="font-mono text-slate-300 mt-0.5">
                        {row.on_tahkim_hedef_tarihi || "—"}
                    </div>
                </div>
            </div>

            {kapali ? (
                <div className="mt-2 text-[10px] text-slate-400 font-semibold">
                    Dosya kapatılmış — tahkime atanamaz
                </div>
            ) : row.gun_gecikme > 0 ? (
                <div className="mt-2 flex items-center gap-1 text-[10px] text-red-400 font-semibold">
                    <AlertTriangle className="h-3 w-3" />
                    {row.gun_gecikme} gün geçti
                </div>
            ) : row.gun_kaldi != null && row.gun_kaldi > 0 ? (
                <div className="mt-2 flex items-center gap-1 text-[10px] text-amber-400">
                    <Clock className="h-3 w-3" /> {row.gun_kaldi} gün kaldı
                </div>
            ) : row.on_tahkim_sure_durumu === "HAZIR" ? (
                <div className="mt-2 text-[10px] text-emerald-400">Bugün süre doldu</div>
            ) : null}

            <div className="mt-3 flex gap-1.5">
                {canApply && (
                    <button
                        type="button"
                        disabled={acting}
                        onClick={() => onUygula(row, false)}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-emerald-600/70 hover:bg-emerald-600 text-white text-[10px] font-semibold disabled:opacity-50"
                    >
                        {acting ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <Send className="h-3 w-3" />
                        )}
                        Ön tahkime geç
                    </button>
                )}
                {row.on_tahkim_sure_durumu === "YAKLASIYOR" && (
                    <button
                        type="button"
                        disabled={acting}
                        onClick={() => onUygula(row, true)}
                        className="px-2 py-1.5 rounded-lg border border-white/15 text-[10px] text-slate-400 hover:bg-white/5 disabled:opacity-50"
                        title="Zorla geçiş"
                    >
                        Zorla
                    </button>
                )}
            </div>
        </article>
    );
}

export function AktifKart({ row }) {
    return (
        <article className="group rounded-xl border border-sky-500/25 bg-gradient-to-br from-sky-500/8 to-[#0B0F19]/90 p-4 hover:border-sky-400/45 transition-all">
            <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                    <Link
                        to={`/e/tahkim-dosyalari/${row.tahkim_id}`}
                        className="text-sm font-mono text-[#F3E5AB] hover:underline block truncate flex items-center gap-1"
                    >
                        {row.dosya_no || `#${row.tahkim_id}`}
                        <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-60 transition" />
                    </Link>
                    <div className="flex items-center gap-1 text-[10px] text-slate-500 mt-0.5">
                        <Car className="h-3 w-3" />
                        {row.plaka || "—"}
                    </div>
                </div>
                <span className="shrink-0 px-2 py-0.5 rounded border border-sky-500/40 bg-sky-500/15 text-[9px] font-bold uppercase text-sky-200">
                    Aktif
                </span>
            </div>

            <div className="space-y-1.5 text-xs">
                <div className="flex items-center gap-1.5 text-slate-400">
                    <User className="h-3 w-3 shrink-0" />
                    <span className="truncate">{row.muvekkil_ad || "—"}</span>
                </div>
                <div className="text-slate-500 truncate">{row.sigorta_sirketi || "—"}</div>
            </div>

            <div className="mt-3 pt-3 border-t border-white/5 space-y-1.5 text-[10px]">
                {row.basvuru_no ? (
                    <div className="flex justify-between">
                        <span className="text-slate-600">Başvuru no</span>
                        <span className="font-mono text-slate-300">{row.basvuru_no}</span>
                    </div>
                ) : null}
                <div className="flex justify-between">
                    <span className="text-slate-600">Hakem</span>
                    <span className={row.hakem ? "text-slate-300" : "text-amber-400"}>
                        {row.hakem || "Atanmadı"}
                    </span>
                </div>
                {row.asama_guncelleme ? (
                    <div className="flex items-center gap-1 text-slate-500">
                        <Calendar className="h-3 w-3" />
                        Geçiş: {row.asama_guncelleme.slice(0, 10)}
                    </div>
                ) : null}
            </div>

            <div className="mt-3 flex gap-1.5">
                <Link
                    to={`/e/tahkim-dosyalari/${row.tahkim_id}`}
                    className="flex-1 text-center px-2 py-1.5 rounded-lg border border-sky-500/30 text-[10px] text-sky-200 hover:bg-sky-500/10 font-semibold"
                >
                    Tahkim dosyası
                </Link>
                {row.sigorta_dosya_id ? (
                    <Link
                        to={`/e/sigorta-dosyalari/${row.sigorta_dosya_id}`}
                        className="px-2 py-1.5 rounded-lg border border-white/10 text-[10px] text-slate-400 hover:bg-white/5"
                    >
                        Sigorta
                    </Link>
                ) : null}
            </div>
        </article>
    );
}
